import { TimestampDisplay } from '@/libs/components/src/lib/TimestampDisplay';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Input } from '@chiroup/components/form/Input';
import { UnroutedExchangeSummary } from '@chiroup/core/types/ConnectorAdmin.type';
import { useState } from 'react';
import {
  useAssignUnrouted,
  useCloseUnrouted,
  useUnroutedExchanges,
} from '../../hooks/useConnectionApi';
import connectionApiService from '../../services/connectionApi.service';

const money = (n: number | null) => (n === null ? '' : n.toFixed(2));

/**
 * Unrouted-document queue (plan §3e.7 / INFRA-5): inbound documents whose
 * clinic could not be determined park here (Unrouted disposition, clinicId 0)
 * — never silently dropped, never guessed. A human routes each row: assign a
 * clinic (re-runs the ingest funnel with clinicId pinned; corroboration still
 * enforced) or close it with a reason. Both are audited server-side.
 *
 * Built ahead of INFRA-5 against the UnroutedExchangeSummary contract; the
 * list renders denormalized `meta` display fields only — never the payload.
 */
export const UnroutedQueue = () => {
  const unrouted = useUnroutedExchanges();
  const assign = useAssignUnrouted();
  const close = useCloseUnrouted();

  const rows = unrouted.data?.rows ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [assignClinicId, setAssignClinicId] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [stamping, setStamping] = useState(false);
  const [stampedId, setStampedId] = useState<string | null>(null);

  const selected: UnroutedExchangeSummary | null =
    rows.find(r => r.exchangeId === selectedId) ?? null;

  // Assign re-runs the ingest funnel — stamp the exchange FIRST and the
  // re-run leaves breadcrumbs (viewable in the ops console's trace log).
  const traceSelected = async () => {
    if (!selected) return;

    setStamping(true);

    try {
      const res = await connectionApiService.stampExchange(selected.exchangeId, 15);

      setStampedId(res.exchangeId);
    } catch {
      window.alert(`Could not stamp '${selected.exchangeId}' for tracing.`);
    } finally {
      setStamping(false);
    }
  };

  const clinicIdNum = Number(assignClinicId.trim());
  const canAssign = !!selected && Number.isInteger(clinicIdNum) && clinicIdNum > 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Unrouted queue{' '}
          <span className="text-base font-normal text-gray-500">
            {unrouted.isLoading ? '…' : `${rows.length} waiting`}
          </span>
        </h1>
      </div>

      {unrouted.isError ? (
        <p className="text-sm text-red-600">
          Could not load the queue — the endpoint is unreachable (it ships with INFRA-5).
        </p>
      ) : unrouted.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length ? (
        <div className="overflow-auto rounded border">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="p-2">Received</th>
                <th className="p-2">Vendor</th>
                <th className="p-2">Doc</th>
                <th className="p-2">Reason</th>
                <th className="p-2">Payor</th>
                <th className="p-2">Payee NPI</th>
                <th className="p-2">TIN</th>
                <th className="p-2">TRN/Check No.</th>
                <th className="p-2 text-right">Amount</th>
                <th className="p-2 text-right">CLPs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr
                  key={r.exchangeId}
                  className={`cursor-pointer border-t align-top ${
                    r.exchangeId === selectedId ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedId(r.exchangeId)}
                >
                  <td className="whitespace-nowrap p-2 font-mono">
                    <TimestampDisplay ts={r.receivedAt} format="YYYY-MM-DD HH:mm:ss" />
                  </td>
                  <td className="p-2">{r.vendor}</td>
                  <td className="p-2 font-mono">{r.docType ?? ''}</td>
                  <td className="p-2">{r.reason}</td>
                  <td className="p-2">{r.payorName ?? ''}</td>
                  <td className="p-2 font-mono">{r.payeeNpi ?? ''}</td>
                  <td className="p-2 font-mono">{r.payeeTin ?? ''}</td>
                  <td className="p-2 font-mono">{r.trn ?? ''}</td>
                  <td className="p-2 text-right font-mono">{money(r.paymentAmount)}</td>
                  <td className="p-2 text-right font-mono">{r.clpCount ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          Queue is empty — every inbound document routed to a clinic.
        </p>
      )}

      {selected ? (
        <div className="mt-4 flex flex-wrap items-end gap-2 border-t pt-4">
          <span className="pb-2 text-sm">
            <span className="font-mono">{selected.exchangeId}</span> —
          </span>
          <Button
            color={ButtonColors.plain}
            loading={stamping}
            onClick={() => traceSelected()}
            tooltip="Stamp this exchange for 15 minutes of tracing, then assign — the re-run leaves breadcrumbs in the ops console."
            text={stampedId === selected.exchangeId ? 'Tracing on' : 'Trace'}
          />
          <div className="w-40">
            <Input
              name="unrouted-clinic-id"
              label="Assign to clinic id"
              value={assignClinicId}
              onChange={(val: string) => setAssignClinicId(val)}
            />
          </div>
          <Button
            color={ButtonColors.primary}
            loading={assign.isLoading}
            disabled={!canAssign}
            onClick={() => {
              if (
                window.confirm(
                  `Assign ${selected.exchangeId} to clinic ${clinicIdNum} and re-run routing?`
                )
              ) {
                assign.mutate(
                  { exchangeId: selected.exchangeId, clinicId: clinicIdNum },
                  { onSuccess: () => setAssignClinicId('') }
                );
              }
            }}
            text="Assign"
          />
          <div className="w-96">
            <Input
              name="unrouted-close-reason"
              label="Close reason"
              value={closeReason}
              onChange={(val: string) => setCloseReason(val)}
            />
          </div>
          <Button
            color={ButtonColors.red}
            loading={close.isLoading}
            disabled={!closeReason.trim()}
            onClick={() => {
              if (window.confirm(`Close ${selected.exchangeId} without routing it?`)) {
                close.mutate(
                  { exchangeId: selected.exchangeId, reason: closeReason.trim() },
                  { onSuccess: () => setCloseReason('') }
                );
              }
            }}
            text="Close"
          />
          <Button
            color={ButtonColors.plain}
            onClick={() => {
              setSelectedId(null);
              setAssignClinicId('');
              setCloseReason('');
            }}
            text="Cancel"
          />
        </div>
      ) : rows.length ? (
        <p className="mt-3 text-xs text-gray-500">Select a row to assign or close it.</p>
      ) : null}
    </div>
  );
};

export default UnroutedQueue;
